import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type ReactFlowState, useStore } from '@xyflow/react'
import { animate } from 'framer-motion'
import { useAtom, useAtomValue } from 'jotai'

import {
  collapsedNodeSizesAtom,
  expandedNodeSizesAtom,
  expandedNodeTypesAtom,
  expandTransitionProgressAtom,
  type NodeSize
} from 'Automations/automationEditor/atoms'
import {
  MAX_ANIMATED_EXPANSION_NODES,
  NODE_EXPAND_TRANSITION
} from 'Automations/automationEditor/constants'
import { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import {
  applyExpandedLayout,
  type NodeOffset
} from 'Automations/automationEditor/utils'
import { NodeType } from 'Automations/nodeDefinitions/types'

// Snapshot of every node's currently-measured size from the ReactFlow store.
// `node.measured` lives in the store, not on the presentation nodes, so the
// expansion layout sources sizes from here.
const measuredSizesSelector = (
  state: ReactFlowState
): Record<string, NodeSize> =>
  // Runs on every store change (every frame mid-transition), so accumulate into
  // one object rather than spreading a fresh copy per node (O(n²) in the node
  // count).
  Array.from(state.nodeLookup.values()).reduce<Record<string, NodeSize>>(
    (acc, node) => {
      const { width, height } = node.measured
      if (width != null && height != null) acc[node.id] = { width, height }
      return acc
    },
    {}
  )

const sizesEqual = (
  a: Record<string, NodeSize>,
  b: Record<string, NodeSize>
): boolean => {
  const aKeys = Object.keys(a)
  if (aKeys.length !== Object.keys(b).length) return false
  return aKeys.every(
    key =>
      b[key] && a[key].width === b[key].width && a[key].height === b[key].height
  )
}

const setsEqual = <T>(a: Set<T>, b: Set<T>): boolean =>
  a.size === b.size && [...a].every(value => b.has(value))

/**
 * Owns the ephemeral, type-keyed node-expansion layout for the canvas.
 *
 * Sizes are sourced from the ReactFlow store (where `measured` lives), collapsed
 * sizes are cached while settled, and a single framer-motion animation per
 * toggle drives `expandTransitionProgressAtom`. Both the rendered-node position
 * and (in `EditorNodeTemplate`) the node's size lerp read that one progress, so
 * a node's visual centre stays fixed at every frame.
 *
 * Returns helpers for the canvas to convert between presentation space (the
 * saved, collapsed-layout coordinates the atoms always hold) and rendered space
 * (what ReactFlow draws, with the expanded offset applied).
 */
export const useNodeExpansionLayout = (
  nodes: AutomationsPresentationV1Node[]
): {
  applyRenderOffset: (
    node: AutomationsPresentationV1Node,
    isDragging: boolean
  ) => { x: number; y: number }
  freezeOffsets: () => void
  removeFrozenOffset: (
    node: AutomationsPresentationV1Node
  ) => AutomationsPresentationV1Node
} => {
  const expandedNodeTypes = useAtomValue(expandedNodeTypesAtom)
  const [collapsedSizes, setCollapsedSizes] = useAtom(collapsedNodeSizesAtom)
  const expandedSizes = useAtomValue(expandedNodeSizesAtom)
  const [progress, setProgress] = useAtom(expandTransitionProgressAtom)
  const measuredSizes = useStore(measuredSizesSelector, sizesEqual)

  // Cache each node's collapsed (settled) size. Only captured while the node's
  // type is collapsed and no transition is running, so it never picks up the
  // animating size.
  useEffect(() => {
    if (progress !== 0) return
    setCollapsedSizes(prev => {
      const next = nodes.reduce((acc, node) => {
        const size = measuredSizes[node.id]
        const isCollapsed = !expandedNodeTypes.has(node.type as NodeType)
        if (!size || !isCollapsed) return acc

        const existing = acc[node.id]
        if (
          existing &&
          existing.width === size.width &&
          existing.height === size.height
        ) {
          return acc
        }
        return { ...acc, [node.id]: size }
      }, prev)

      return next === prev ? prev : next
    })
  }, [measuredSizes, nodes, expandedNodeTypes, progress, setCollapsedSizes])

  // A node inserted while its type is already expanded is "born expanded": it
  // renders expanded straight away, so its collapsed size is never measured
  // (that only happens while settled at progress 0). Seed it from a same-type
  // sibling's collapsed size — collapsed size is type-determined — so its first
  // transition animates instead of snapping. The real size replaces this seed
  // once the node settles collapsed.
  useEffect(() => {
    setCollapsedSizes(prev => {
      const representativeByType = nodes.reduce<
        Partial<Record<NodeType, NodeSize>>
      >((acc, node) => {
        const type = node.type as NodeType
        if (acc[type] || !prev[node.id]) return acc
        return { ...acc, [type]: prev[node.id] }
      }, {})

      const next = nodes.reduce((acc, node) => {
        const type = node.type as NodeType
        const representative = representativeByType[type]
        const needsSeed = expandedNodeTypes.has(type) && !acc[node.id]
        if (!needsSeed || !representative) return acc
        return { ...acc, [node.id]: representative }
      }, prev)

      return next === prev ? prev : next
    })
  }, [nodes, expandedNodeTypes, setCollapsedSizes])

  // The set of types whose offsets are currently applied to the layout. It
  // tracks `expandedNodeTypes` on expand, but lingers through the collapse
  // animation so the offsets stay until progress reaches 0.
  const [layoutExpandedTypes, setLayoutExpandedTypes] = useState<Set<NodeType>>(
    new Set()
  )
  const animationRef = useRef<ReturnType<typeof animate> | null>(null)
  // The expansion target the in-flight animation is converging on. Guards
  // against restarting the animation when unrelated deps (e.g. `nodes`, which
  // ReactFlow rewrites every frame as the animating node's dimensions change)
  // re-run the effect mid-transition.
  const animationTargetRef = useRef<Set<NodeType> | null>(null)

  const offsets = useMemo(
    () =>
      applyExpandedLayout({
        nodes,
        expandedNodeTypes: layoutExpandedTypes,
        collapsedSizes,
        expandedSizes
      }).offsets,
    [nodes, layoutExpandedTypes, collapsedSizes, expandedSizes]
  )

  // Drive a single progress animation per toggle. Inert until both sizes for
  // the toggled type are known.
  useEffect(() => {
    if (setsEqual(expandedNodeTypes, layoutExpandedTypes)) {
      animationTargetRef.current = null
      return
    }

    // Already animating toward this exact target — let it finish.
    if (
      animationTargetRef.current &&
      setsEqual(animationTargetRef.current, expandedNodeTypes)
    ) {
      return
    }

    const changedTypes = new Set<NodeType>(
      [...expandedNodeTypes, ...layoutExpandedTypes].filter(
        type => expandedNodeTypes.has(type) !== layoutExpandedTypes.has(type)
      )
    )
    // Gate only on the lazily-measured expanded size. The collapsed size is
    // captured eagerly while settled and seeded for born-expanded nodes (above),
    // so it's effectively always known — gating on it too would risk jamming the
    // toggle if a seed were ever unavailable, without buying anything.
    const sizesReady = nodes
      .filter(node => changedTypes.has(node.type as NodeType))
      .every(node => expandedSizes[node.id])
    if (!sizesReady) return

    animationRef.current?.stop()
    animationTargetRef.current = expandedNodeTypes
    const isExpanding = expandedNodeTypes.size > layoutExpandedTypes.size

    // The animation re-lays out every node each frame; past a handful of nodes
    // of the toggled type that's too heavy, so snap straight to the target.
    const togglingNodeCount = nodes.filter(node =>
      changedTypes.has(node.type as NodeType)
    ).length
    const shouldAnimate = togglingNodeCount <= MAX_ANIMATED_EXPANSION_NODES

    if (isExpanding) {
      // Apply the target layout immediately, then ramp in (or snap to) the size.
      setLayoutExpandedTypes(expandedNodeTypes)
      if (shouldAnimate) {
        animationRef.current = animate(0, 1, {
          ...NODE_EXPAND_TRANSITION,
          onUpdate: setProgress
        })
      } else {
        setProgress(1)
      }
    } else if (shouldAnimate) {
      // Keep the expanded offsets until the shrink finishes, then drop them.
      animationRef.current = animate(1, 0, {
        ...NODE_EXPAND_TRANSITION,
        onUpdate: setProgress,
        onComplete: () => {
          setLayoutExpandedTypes(expandedNodeTypes)
          setProgress(0)
        }
      })
    } else {
      // Collapse with no animation: drop the offsets and reset progress at once.
      setLayoutExpandedTypes(expandedNodeTypes)
      setProgress(0)
    }
  }, [
    expandedNodeTypes,
    layoutExpandedTypes,
    nodes,
    expandedSizes,
    setProgress
  ])

  useEffect(() => () => animationRef.current?.stop(), [])

  // Offsets are frozen at drag start so the offset sweep can't reorder as the
  // dragged node moves, keeping the rendered↔presentation round-trip exact for
  // the duration of the drag.
  const frozenOffsetsRef = useRef<{
    offsets: Record<string, NodeOffset>
    progress: number
  }>({ offsets: {}, progress: 0 })

  const freezeOffsets = useCallback(() => {
    frozenOffsetsRef.current = { offsets, progress }
  }, [offsets, progress])

  // Presentation → rendered: add the live offset, scaled by progress. While
  // dragging, use the frozen offsets so a node moving past a neighbour can't
  // reorder the sweep and shift the others.
  const applyRenderOffset = useCallback(
    (node: AutomationsPresentationV1Node, isDragging: boolean) => {
      const offset = isDragging
        ? frozenOffsetsRef.current.offsets[node.id]
        : offsets[node.id]
      if (!offset) return node.position
      return {
        x: node.position.x + offset.dx * progress,
        y: node.position.y + offset.dy * progress
      }
    },
    [offsets, progress]
  )

  // Rendered → presentation: subtract the frozen offset of a repositioned node
  // so what lands in the atoms during a drag is always presentation space.
  const removeFrozenOffset = useCallback(
    (node: AutomationsPresentationV1Node) => {
      const { offsets: frozenOffsets, progress: frozenProgress } =
        frozenOffsetsRef.current
      const offset = frozenOffsets[node.id]
      if (!offset) return node
      return {
        ...node,
        position: {
          x: node.position.x - offset.dx * frozenProgress,
          y: node.position.y - offset.dy * frozenProgress
        }
      }
    },
    []
  )

  return { applyRenderOffset, freezeOffsets, removeFrozenOffset }
}
